'use client'

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject
} from 'react'
import { createPortal } from 'react-dom'
import { datadogLogs } from '@datadog/browser-logs'
import type { TFunction } from 'i18next'
import { fromInteger } from '@repo/money'
import { buildBirdieCardView } from 'src/components/BirdieRoundCard/birdieCardModel'
import { birdieBoardCopy } from 'src/components/BirdieRoundCard/birdieCopy'
import {
  BOARD_MISSING_GRACE_MS,
  boardTelemetryDecision,
  patternIdByKey,
  paytableInputsFromOdds,
  selectRoundLocation,
  selectRoundOdds
} from 'src/components/BirdieRoundCard/roundOdds'
import { chargeableStake } from 'src/components/ChipPlacementPanel/chargeableStake'
import { ChipStrip } from 'src/components/ChipPlacementPanel/ChipStrip'
import { roundTransitionAction } from 'src/components/ChipPlacementPanel/roundTransitionAction'
import {
  SUBMIT_TIMER_MS,
  classifySubmitOutcome,
  isFrozenSubmission,
  shouldRecoverMissedRoundReset
} from 'src/components/ChipPlacementPanel/submissionLifecycle'
import { TotalWinBanner } from 'src/components/ChipPlacementPanel/TotalWinBanner'
import { submitBackstopDelayMs } from 'src/components/BirdieBoard/submitBackstop'
import type { WinTier } from 'src/components/ChipPlacementPanel/WinPill'
import { BET_CONFIG } from 'src/config'
import { isClaimed, useInventoryStore } from 'src/features/inventory'
import { useChipPlacementBetSubmit } from 'src/hooks/useChipPlacementBetSubmit'
import { useBetLock, type BetLockSent } from 'src/hooks/useBetLock'
import { RejectionDetails } from 'src/types/domain/AppError'
import { useStageTwo } from 'src/hooks/useStageTwo'
import { stageTwoAt } from 'src/utils/betLock'
import { hardCloseClock, hardCountdown, perfNow } from 'src/utils/hardCloseClock'
import { useDeviceInfo } from 'src/hooks/useDeviceInfo'
import { useGetCurrentBets } from 'src/hooks/useGetCurrentBets'
import { useRoundNotOpen } from 'src/hooks/useRoundNotOpen'
import { useSoundEffect } from 'src/hooks/useSoundEffect'
import { preloadBirdieEffects } from 'src/components/Sound/birdieEffects'
import { paytableHash } from 'src/lib/fairness/birdie'
import { useClientSideTranslation } from 'src/services/i18n'
import { getTranslatedMarbleColor } from 'src/services/i18n/utils/gameTranslations'
import { useBetStore } from 'src/store/bet'
import {
  useBirdiePutts,
  useBirdiePuttsActions,
  useBirdieRoundType,
  useBirdieRoundTypeAtMs
} from 'src/store/birdiePutts'
import { useBirdiePublishLiveBoard } from 'src/store/birdieLiveBoard'
import { useGetCurrentLanguage, useGetGameConfig } from 'src/store/game'
import { useGetPlayerLevel, useIsLevelSystemEnabled } from 'src/store/playerLevel'
import type { MarbleGame } from 'src/types/domain/Game'
import { isKnownGameConfig } from 'src/types/domain/GameConfig'
import type { AuthenticatedPlayer } from 'src/types/domain/Player'
import type { Race } from 'src/types/domain/Race'
import { isRaceSettledEvent, type RaceEvent } from 'src/types/domain/RaceEvent'
import { formatNumberWithSuffix } from 'src/utils/formatNumberWithSuffix'
import {
  generateChipsFromMultipliers,
  generateSmartChips,
  hasCustomChipMultipliers,
  prependMinBetChip
} from 'src/utils/generateSmartChips'
import { getCurrencySymbol } from 'src/utils/getCurrencySymbol'
import { ArcOverlay } from './ArcOverlay'
import { birdieAutoBetCopy, autoBetSkipText, autoBetZoneText } from './autoBetCopy'
import { boardGeometryFor, cardBoxPx, frameScale, type BoardLayoutMode } from './boardGeometry'
import { avatarRing } from './avatarRing'
import './birdie-board.scss'
import { BirdieAutoBetOpener } from './BirdieAutoBetOpener'
import { BirdieAutoBetPill } from './BirdieAutoBetPill'
import { BirdieAutoBetSheet } from './BirdieAutoBetSheet'
import {
  DEFAULT_RULE,
  autoBetEntryVisible,
  ghostIdsFor,
  ruleOnLadder,
  type AutoBetRule,
  type AutoBetSkip
} from './birdieAutoBet'
import type { AutoBetStopReason } from './birdieAutoBetMachine'
import { BirdieCard } from './BirdieCard'
import {
  boardMoved,
  canRepeat,
  optionMaxBets,
  type BirdieChipAction,
  type ChipClamp,
  type ChipNotice,
  type ChipOutcome
} from './birdieChips'
import {
  EMPTY_INVENTORY_STATE,
  applyChipAction,
  availableBoosters,
  availableFreeBets,
  boostedMultipliers,
  boosterPercentById,
  freeBetOptionIds,
  plannedClaim,
  type BirdieInventoryState,
  type PlannedClaim
} from './birdieInventory'
import { applyRoundOdds, repeatClampNotice, type PricedMarket } from './birdieMarkets'
import { BIRDIE_ARC_SLOT_CLASS } from './boardFor'
import {
  BIRDIE_RESULT_HOLD_MS,
  BIRDIE_RING_EASE_MS,
  boardPhase,
  boardSeated,
  latestEventType,
  settlementRevealDelayMs
} from './boardPhase'
import { capsuleState } from './capsuleState'
import { chipStyleAt, chipStyleForAmount, type ChipStyle } from './chipPalette'
import {
  FLYING_CHIP_LIFETIME_MS,
  FlyingChipLayer,
  RECEIVING_PULSE_MS,
  flightPath,
  landingDelayMs,
  prefersReducedMotion,
  type FlyingChipSpec
} from './FlyingChip'
import {
  nextPuttRevealDelayMs,
  ringDelaysForLive,
  roundTypeVisible,
  type BirdieRingsView
} from './livePutts'
import { holedCount, liveStatus } from './liveStatus'
import { BirdieCallout } from './BirdieCallout'
import { BirdieTeeOff } from './BirdieTeeOff'
import { useBirdieCues } from './birdieCues'
import { birdieCalloutCopy } from './calloutCopy'
import { teeOffCountdown } from './teeOff'
import { useBirdieCallout } from './useBirdieCallout'
import { useGolferThought } from './useGolferThought'
import { RING_STAGGER_MS, roundTypeWinnerId, settlementView } from './settlement'
import { StackBoard } from './StackBoard'
import {
  missedReadyRecovery,
  resolveTrackedBets,
  restorableChips,
  stuckAtSettlement,
  trackSubmittedBets,
  type TrackedBets
} from './submissionReconcile'
import { buildTileViews, formatMultiplierText, type Translate } from './tileViewModel'
import { useBirdieAutoBet, type UseBirdieAutoBet } from './useBirdieAutoBet'
import { useBirdieChips } from './useBirdieChips'
import { useStageFrame } from './useStageFrame'
import { frameIsUsable } from './stageFrame'

export interface BirdieBoardStats {
  totalStaked: number
  totalPotentialWin: number
  chipCount: number
}

type Props = Readonly<{
  game: MarbleGame
  player: AuthenticatedPlayer
  onStatsChange?: (stats: BirdieBoardStats) => void
  /**
   * The phone's past-rounds rail, handed straight to `<StackBoard>` (owner
   * 2026-09-17: the flag and the discs ride the capsule's row so the board wins
   * back a band of vertical space). Rendered by the stack ONLY, so the desktop
   * arc never sees it and the page keeps its own mount for every other game.
   */
  railSlot?: ReactNode
}>

interface BoardChip extends ChipStyle {
  /** Stable React key — chip values are de-duplicated, so this is unique. */
  id: string
  value: number
}

/**
 * De-duplicates (a repeated value would be a duplicate React key) and paints
 * the palette on in order.
 */
const toChips = (values: readonly number[]): BoardChip[] => {
  const unique: number[] = []
  for (const value of values) if (!unique.includes(value)) unique.push(value)
  return unique.map((value, index) => ({ id: `chip-${value}`, value, ...chipStyleAt(index) }))
}

/** B34: the gap between the corner capsule and the putt callout, reference px. */
const CALLOUT_GAP_PX = 8
const FALLBACK_CHIP_VALUES = [0.1, 0.5, 1, 5, 10, 25, 50]
/** The arc is narrower than the shared panel's strip, so six chips, not seven. */
const CHIP_TARGET_COUNT = 6

/**
 * Host tick. 1 Hz is enough: phase changes are event-driven, the hairline's
 * drain is a CSS transition over exactly this interval (B11 item 4), and the
 * settlement reveal has its own one-shot timer (B11 item 7) rather than waiting
 * for the next tick. What is left on this clock is the live per-putt rings,
 * whose ±1 s is inside their own hold's slack.
 */
const TICK_MS = 1000
/** How often to re-find the portal slot (it is re-created with `.dolby-feed`). */
const SLOT_PROBE_MS = 500
const NOTICE_LIFETIME_MS = 2500
/** Matches the shared panel's banner cadence (`ChipPlacementPanel.tsx:1156-1166`). */
const TOTAL_WIN_DELAY_MS = 950
const WIN_CHIME_LEAD_MS = 250
const MEGA_CHIME_GAP_MS = 320
/**
 * How long the shared strip's ×2 punch keyframe runs before the flag is
 * cleared, so the next tap can re-trigger it — `action-punch 0.45s` in
 * `chip-placement.scss`, with the panel's own 50 ms of slack
 * (`ChipPlacementPanel.tsx:1669`).
 */
const DOUBLE_PUNCH_MS = 500

// B32: the strip's auto-bet toggle is live for Birdie — it arms a betting ZONE
// (`useBirdieAutoBet`), not a repeat of last round's board.

const EMPTY_EVENTS: readonly RaceEvent[] = []

/**
 * The win-pill size/glow tier. A copy of `tierForBet`
 * (`ChipPlacementPanel.tsx:209-216`), which is module-private there.
 */
function tierForBet(bet: { status: string; betAmount: number; potentialPayout: number }): WinTier {
  if (bet.status !== 'won' || bet.betAmount <= 0) return 'win'
  const ratio = bet.potentialPayout / bet.betAmount
  if (ratio >= 25) return 'mega'
  if (ratio >= 10) return 'big'
  return 'win'
}

/**
 * The refusal copy. One literal translation call per branch, because the i18n
 * extractor is literal-only (A25) — a `Record<ChipNotice, string>` fed
 * through `t()` would never reach the locale files and every non-English viewer
 * would read raw English. `repeat-clamped` carries its own pluralised sentence
 * (`repeatClampNotice`) and is handled before this is reached.
 */
function noticeCopy(key: ChipNotice, t: Translate): string {
  if (key === 'balance-loading') return t('Initializing balance')
  if (key === 'option-max') return t('Max reached on this option')
  if (key === 'insufficient-balance') return t('Insufficient balance')
  if (key === 'partial-double') return t('Some chips hit this round’s max')
  if (key === 'double-maxed') return t('Max reached on all options')
  if (key === 'nothing-to-repeat') return t('No valid options to repeat')
  // The shared panel's own sentence, reworded only to fit this file's rule that
  // every board string is a literal `t()` key (`ChipPlacementPanel.tsx:1264`
  // shows it untranslated). A free bet IS the bet: one item, one fixed value.
  if (key === 'free-bet-locked') return t('Free bet placed — no extra stake on this option')
  // Deliberately the persistent board notice's own sentence rather than a new
  // key: it is already translated in all 16 locales, and it says exactly why
  // the refusal happened.
  if (key === 'round-unpriced') return birdieBoardCopy(t as unknown as TFunction).waitingForOdds
  return ''
}

/**
 * The Birdie betting surface. Mounted from `page.tsx` in the bottom column (so
 * the chip bar sits where every other game's bar sits) and portalling its arc
 * overlay into the slot div page.tsx renders next to `<LiveStream>`, so the
 * tiles land on the VIDEO box rather than on the CSS-scaled bottom column.
 *
 * It reads the same state the shared panel reads and honours the same
 * `onStatsChange` contract, but owns its own placement reducer (B4) and a slim
 * submission lifecycle built from the shared PURE helpers — `ChipPlacementPanel`
 * itself is never imported, mounted or modified for a Birdie game.
 */
export function BirdieBoard({ game, player, onStatsChange, railSlot }: Props) {
  const gameConfig = useGetGameConfig()
  const language = useGetCurrentLanguage()
  // `useClientSideTranslation` widens to `any` (it forwards an `any` options
  // bag to react-i18next), so the one thing this board needs off it is pinned
  // to the `Translate` shape the board's pure modules already speak.
  const { t: rawT }: { t: Translate } = useClientSideTranslation(language)

  /**
   * B11 item 10 / review 16: `useClientSideTranslation` returns a `t` whose
   * identity NEVER changes — it is a `useCallback(…, [])` over a ref
   * (`i18n/client.ts:53-59`), deliberately, so the player-channel listeners stop
   * thrashing. Every memo below was keyed on that `t`, so a runtime language
   * switch left the tiles, the captions and the persistent notice rendering the
   * previous language until the next round rebuilt them from scratch.
   *
   * This wrapper is the one value keyed on `language` itself, and it pins the
   * lookup to that language rather than trusting i18next's global state to have
   * finished switching — so a memo keyed on it can never cache a string from
   * the language it is no longer keyed to.
   */
  const t = useMemo<Translate>(() => {
    // Lower-cased because i18next is configured `lowerCaseLng: true`
    // (`getI18nSettings.ts:38`) and normalises whatever `changeLanguage` was
    // handed. Without this a `?language=DE` query param would keep resolving
    // for the rest of the widget and fall back to English for this board alone.
    const lng = language.toLowerCase()
    return (key?: string, options?: Record<string, unknown>) => rawT(key, { lng, ...options })
  }, [language, rawT])
  const { isMobile } = useDeviceInfo()
  const { playEffect } = useSoundEffect()
  const { currentBets } = useGetCurrentBets()
  const { submitAllBets } = useChipPlacementBetSubmit({ game, player })

  const currency = player.balance.currency
  const currencySymbol = getCurrencySymbol(currency)
  // `NonEmptyArray` is a mapper-level claim; widening to a plain array keeps
  // `races[0]` honestly `Race | undefined` under `noUncheckedIndexedAccess`,
  // so an empty round list degrades to "no board" instead of throwing.
  const races: readonly Race[] = game.races
  const currentRace = races[0]
  const raceEvents = useMemo<readonly RaceEvent[]>(
    () => currentRace?.raceEvents ?? EMPTY_EVENTS,
    [currentRace]
  )

  // ── The round's numbers ────────────────────────────────────────────────
  const roundOdds = useMemo(() => selectRoundOdds(currentRace), [currentRace])
  const started = useMemo(() => selectRoundLocation(currentRace), [currentRace])
  const marbleNumberById = useMemo(
    () => new Map(game.marbles.map((marble) => [marble.uuid, parseInt(marble.number, 10)])),
    [game.marbles]
  )
  const keyById = useMemo(() => {
    const byKey = patternIdByKey(game.marbles)
    const out: Record<string, string> = {}
    for (const key of Object.keys(byKey)) {
      const id = byKey[key]
      if (id !== undefined) out[id] = key
    }
    return out
  }, [game.marbles])

  const markets: PricedMarket[] = useMemo(() => {
    const base = isKnownGameConfig(gameConfig) && gameConfig.markets ? gameConfig.markets : []
    return applyRoundOdds(base, roundOdds, marbleNumberById)
  }, [gameConfig, marbleNumberById, roundOdds])

  const allOptions = useMemo(() => markets.flatMap((market) => market.options), [markets])
  // Go pays the player-level bonus on every Birdie bet, so the tiles and the
  // Potential Win pill price it too — the shared panel's rule exactly
  // (`ChipPlacementPanel.tsx:649-652`: `multiplierBonus` is a percentage).
  // Without it the board priced at level 0 while Go paid up to 1 % more (B27
  // item 22). Re-renders when a level-up lands between rounds, like the panel.
  const isLevelSystemEnabled = useIsLevelSystemEnabled()
  const playerLevel = useGetPlayerLevel()
  const levelBoostFactor =
    isLevelSystemEnabled && playerLevel?.multiplierBonus ? 1 + playerLevel.multiplierBonus / 100 : 1
  const marketIdByOption = useMemo(() => {
    const out = new Map<string, string>()
    for (const market of markets) for (const option of market.options) out.set(option.id, market.id)
    return out
  }, [markets])
  // Memoised on purpose: `useBirdieChips` recomputes `totals` — and therefore
  // fires `onStatsChange` — whenever this map's identity changes.
  //
  // PRICED options only. `applyRoundOdds` zeroes anything this round did not
  // price, and an absent entry is what tells `totalPotentialWin` the multiplier
  // is UNKNOWN (skip the chip) rather than zero (a real "pays nothing").
  const multiplierById = useMemo(
    () =>
      new Map(
        allOptions
          .filter((option) => option.bettable && option.multiplier > 0)
          .map((option) => [option.id, option.multiplier * levelBoostFactor])
      ),
    [allOptions, levelBoostFactor]
  )

  // ── Rewards the player holds (B38) ─────────────────────────────────────
  // The two pools and the claims bound out of them. Everything about WHICH item
  // may go on WHICH option is `birdieInventory`'s; the board only supplies the
  // game, the wallet's currency and this game's bet window, and spends the
  // answers. There is no inventory pill on a Birdie game (owner 2026-09-17: the
  // chip-placement games do not carry one) — the board's own tap is the whole
  // affordance, and a held free bet simply lands as the first chip.
  // 🚨 NOT `useInventoryItems()`. That hook selects with an inline arrow that
  // returns `state.items.filter(...)` — a NEW array on every call — and zustand
  // cannot memoise it: the selector's identity changes each render, so the
  // filtered array is rebuilt, `useSyncExternalStore` sees a snapshot it has
  // never seen, and React re-renders again. It never reaches a commit, which is
  // why the board's own effects showed no extra runs while the console filled
  // with "Maximum update depth exceeded" (measured on the dev server,
  // 2026-09-18: 514 warnings against 5 effect runs in the same 35 seconds).
  //
  // The RAW array is a stable reference between store writes, so the same list
  // is built here instead, once per actual inventory change.
  const rawInventory = useInventoryStore((state) => state.items)
  const inventoryItems = useMemo(() => rawInventory.filter(isClaimed), [rawInventory])
  const betWindow = useMemo(() => {
    const min = isKnownGameConfig(gameConfig)
      ? fromInteger(gameConfig.bet.minBetAmount, currency)
      : 0
    const max =
      isKnownGameConfig(gameConfig) && gameConfig.bet.maxBetAmount > 0
        ? fromInteger(gameConfig.bet.maxBetAmount, currency)
        : Number.POSITIVE_INFINITY
    return { min, max }
  }, [currency, gameConfig])
  const boosterPool = useMemo(
    () => availableBoosters(inventoryItems, game.id),
    [game.id, inventoryItems]
  )
  const freeBetPool = useMemo(
    () =>
      availableFreeBets(inventoryItems, {
        gameId: game.id,
        currency,
        minBet: betWindow.min,
        maxBet: betWindow.max
      }),
    [betWindow.max, betWindow.min, currency, game.id, inventoryItems]
  )
  const [inventory, setInventory] = useState<BirdieInventoryState>(EMPTY_INVENTORY_STATE)
  // Latest-ref, the board's B3 pattern: the claim for a tap is read between
  // renders, and two dispatches in one tick must both see the first one's result.
  const inventoryRef = useRef(inventory)
  inventoryRef.current = inventory
  const freeBetIds = useMemo(() => freeBetOptionIds(inventory), [inventory])
  const boosterPercents = useMemo(() => boosterPercentById(inventory), [inventory])

  // ── Lightning ──────────────────────────────────────────────────────────
  const latestType = useMemo(() => latestEventType(raceEvents), [raceEvents])
  const revealAllowed =
    latestType === 'race_started' || latestType === 'race_ended' || latestType === 'race_settled'
  const boostedIds = useMemo(
    () =>
      new Set(
        revealAllowed && currentRace !== undefined && currentRace.isLightningRound
          ? (currentRace.lightningBoostedItems ?? [])
          : []
      ),
    [currentRace, revealAllowed]
  )
  const boostPercent = revealAllowed ? (currentRace?.lightningBoostPercent ?? 0) : 0

  const tiles = useMemo(
    () =>
      buildTileViews({
        options: allOptions,
        keyById,
        t,
        boostedIds,
        boostPercent,
        levelBoostFactor,
        boosterPercentById: boosterPercents
      }),
    [allOptions, boostPercent, boostedIds, boosterPercents, keyById, levelBoostFactor, t]
  )

  // ── Limits + chips ─────────────────────────────────────────────────────
  const caps = useMemo(() => {
    if (!isKnownGameConfig(gameConfig)) {
      return {
        maxBetAmount: Number.POSITIVE_INFINITY,
        pickWinnerLimit: Number.POSITIVE_INFINITY,
        maxPayout: Number.POSITIVE_INFINITY
      }
    }
    // `0` in the config means "no limit" (ChipPlacementPanel.tsx:1010-1025).
    const orInfinity = (value: number): number =>
      value > 0 ? fromInteger(value, currency) : Number.POSITIVE_INFINITY
    return {
      maxBetAmount: orInfinity(gameConfig.bet.maxBetAmount),
      pickWinnerLimit: orInfinity(gameConfig.bet.limits.PickWinner.max),
      maxPayout: orInfinity(gameConfig.bet.maxPayout)
    }
  }, [currency, gameConfig])

  // `tiles` is already the priced-AND-bettable-AND-drawable set (`applyRoundOdds`
  // marks an option the round did not price non-bettable, `buildTileViews` drops
  // the ones with no canonical key), so it — not `allOptions` — is what may be
  // bet on, repeated onto or carried forward into.
  const limits = useMemo(
    () => ({
      // Deliberately the ROUND's price, not the boosted one — the shared panel
      // computes `optionMaxBet` from `option.multiplier` alone and leaves the
      // inventory boost out of the ceiling (`ChipPlacementPanel.tsx:1027-1040`),
      // which is the cap Go's `validateChipPlacementBetAmount` enforces too.
      optionMax: optionMaxBets(allOptions, caps),
      balance: player.balance.amount,
      balanceLoaded: player.balanceLoaded,
      bettableIds: new Set(tiles.map((tile) => tile.optionId)),
      roundPriced: tiles.length > 0,
      freeBetOptionIds: freeBetIds
    }),
    [allOptions, caps, freeBetIds, player.balance.amount, player.balanceLoaded, tiles]
  )

  const [notice, setNotice] = useState<{
    message: string
    tone: 'warning' | 'info' | 'error'
  } | null>(null)
  const showNotice = useCallback(
    (message: string, tone: 'warning' | 'info' | 'error' = 'warning') => {
      if (!message) return
      // Drop, then re-raise on the next frame so an identical repeated message
      // replays its entry animation instead of sitting there unchanged.
      setNotice(null)
      requestAnimationFrame(() => {
        setNotice({ message, tone })
      })
    },
    []
  )
  useEffect(() => {
    if (!notice) return undefined
    const timer = setTimeout(() => {
      setNotice(null)
    }, NOTICE_LIFETIME_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [notice])

  const onChipNotice = useCallback(
    (key: ChipNotice, clamped?: ChipClamp[]) => {
      playEffect('cancel')
      if (key === 'repeat-clamped') {
        const message = repeatClampNotice(clamped ?? [], t as unknown as TFunction)
        if (message) showNotice(message, 'warning')
        return
      }
      showNotice(noticeCopy(key, t), 'warning')
    },
    [playEffect, showNotice, t]
  )

  // The Potential Win pill prices a boosted option at its boosted price, for the
  // ONE option the booster is bound to — the shared panel's rule exactly
  // (`ChipPlacementPanel.tsx:1092-1098`), and the price Go will pay it at.
  const pricedById = useMemo(
    () => boostedMultipliers(multiplierById, inventory),
    [inventory, multiplierById]
  )
  const board = useBirdieChips({
    limits,
    multiplierById: pricedById,
    keyById,
    onNotice: onChipNotice
  })

  /**
   * The board's ONE dispatch (B38). Every action goes through it — the taps, the
   * strip, the round transitions, the reject reconcile and the auto-bet plan —
   * because the claims are stepped from the same action and the same before/after
   * boards, and a dispatch that skipped this would leave a free bet bound to an
   * option whose chip is gone.
   *
   * `claim` is only ever supplied by `handlePlace`: an auto-bet placement is not
   * a decision to spend a reward (see `plannedClaim`'s call site).
   */
  const { dispatch: chipDispatch, stateRef: boardStateRef } = board
  const dispatch = useCallback(
    (action: BirdieChipAction, claim?: PlannedClaim): ChipOutcome => {
      const before = boardStateRef.current
      const outcome = chipDispatch(action)
      const next = applyChipAction(inventoryRef.current, {
        action,
        before,
        after: outcome.state,
        ...(claim === undefined ? {} : { claim })
      })
      if (next !== inventoryRef.current) {
        inventoryRef.current = next
        setInventory(next)
      }
      return outcome
    },
    [boardStateRef, chipDispatch]
  )
  // B3 latest-ref: the reject reconcile below subscribes to the bet store ONCE
  // at mount and must dispatch through the current board, not the one that
  // happened to be in scope then.
  const boardDispatchRef = useRef(dispatch)
  boardDispatchRef.current = dispatch
  // Read by the mounted-once reject watcher below, so a language change reaches it.
  const showNoticeRef = useRef(showNotice)
  showNoticeRef.current = showNotice
  const notInTimeTextRef = useRef('')
  notInTimeTextRef.current = t('toast.errors.notInTime')
  // B32: the auto-bet hook is called further down — after the `race_ready`
  // effect, which is the point — so the handlers above it that have to tell
  // the rule about a hand on the board, and the submission that has to tell
  // it what went to the wire, reach it through this ref.
  const autoBetRef = useRef<UseBirdieAutoBet | null>(null)

  // ── Chip strip (compact copy of ChipPlacementPanel.tsx:319-393) ─────────
  const initialBalanceRef = useRef<number | null>(null)
  if (initialBalanceRef.current === null || player.balance.amount > initialBalanceRef.current) {
    initialBalanceRef.current = player.balance.amount
  }
  // Non-null after the block above; read into a value so the chips memo has an
  // honest dependency (the strip must widen when the real balance lands).
  const initialBalance: number = initialBalanceRef.current
  const chips = useMemo(() => {
    if (!isKnownGameConfig(gameConfig)) return toChips(FALLBACK_CHIP_VALUES)
    const minBet = fromInteger(gameConfig.bet.minBetAmount, gameConfig.bet.currency)
    const maxBet = fromInteger(gameConfig.bet.maxBetAmount, gameConfig.bet.currency)
    if (minBet <= 0 || maxBet <= 0) return toChips(FALLBACK_CHIP_VALUES)
    const raw = hasCustomChipMultipliers(gameConfig.bet.chipMultipliers)
      ? generateChipsFromMultipliers(gameConfig.bet.chipMultipliers, minBet)
      : generateSmartChips({
          minBet,
          maxBet,
          balance: initialBalance,
          targetCount: CHIP_TARGET_COUNT
        })
    const filtered = prependMinBetChip(
      raw.filter((value) => value >= minBet && value <= maxBet),
      minBet
    )
    // Balance below the min bet: still show the min so the strip is never
    // empty; the reducer's own guard blocks the placement.
    return toChips(filtered.length === 0 ? [minBet] : filtered)
  }, [gameConfig, initialBalance])

  const [chipIndex, setChipIndex] = useState(0)
  const safeChipIndex = Math.min(chipIndex, chips.length - 1)
  const selectedChip = chips[safeChipIndex] ?? chips[0]
  // Handed to `<ChipStrip>` so the flying chip can launch from the active
  // disc's rect — the same handle the shared panel keeps
  // (`ChipPlacementPanel.tsx:294`).
  const chipBarRef = useRef<HTMLDivElement>(null)
  // The board's own root, the last place focus can go when a control that
  // opened a dialog has been unmounted by the time it closes.
  const boardRef = useRef<HTMLDivElement>(null)
  // Drives the shared strip's one-shot ×2 punch. Cleared and re-set across two
  // animation frames so the keyframe restarts on rapid taps, exactly as the
  // panel does it (`ChipPlacementPanel.tsx:1655-1672`).
  const [doubleAnimAt, setDoubleAnimAt] = useState(0)
  useEffect(() => {
    if (doubleAnimAt === 0) return undefined
    const timer = setTimeout(() => {
      setDoubleAnimAt(0)
    }, DOUBLE_PUNCH_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [doubleAnimAt])

  // ── Flying chip + receiving pulse ──────────────────────────────────────
  const [flying, setFlying] = useState<readonly FlyingChipSpec[]>([])
  const [receivingOptionId, setReceivingOptionId] = useState<string | null>(null)
  const receivingStartTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const receivingClearTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handlePlace = useCallback(
    (optionId: string, target: DOMRect) => {
      const marketId = marketIdByOption.get(optionId)
      if (marketId === undefined) return
      // A free bet IS the bet: one item of one fixed value, which the backend
      // cannot split or top up (`ChipPlacementPanel.tsx:1261-1266`). Answered
      // from the REF rather than left to the reducer's own guard, which reads
      // the claim through `limits` and is therefore a render behind.
      if (inventoryRef.current.freeBets[optionId] !== undefined) {
        onChipNotice('free-bet-locked')
        return
      }
      // What this tap may take, asked BEFORE the placement because a free bet
      // decides the stake: the option locks to the reward's amount rather than
      // to the chip the player has selected, even when that chip is larger
      // (`ChipPlacementPanel.tsx:1273-1281`).
      const claim = plannedClaim({
        state: inventoryRef.current,
        optionId,
        firstChipOnOption: boardStateRef.current.chips[optionId] === undefined,
        boosters: boosterPool,
        freeBets: freeBetPool,
        optionMax: limits.optionMax.get(optionId) ?? Number.POSITIVE_INFINITY
      })
      const free = claim?.freeBet
      const amount = free ? free.amount : (selectedChip?.value ?? 0)
      if (amount <= 0) return
      // Dispatch FIRST: the reducer is the only authority on whether the chip
      // is allowed, and a refused placement must not launch a flight — nor spend
      // the reward, which `applyChipAction` only binds once the board moved.
      const outcome = dispatch(
        free
          ? { type: 'place', optionId, marketId, amount, freeBet: true }
          : { type: 'place', optionId, marketId, amount },
        claim
      )
      if (outcome.notice) return
      // A hand on the board turns auto bet off; the chips stay (B32). AFTER the
      // refusal gate: a placement the reducer refused changed nothing, so it is
      // no hand on the board — and its notice would bury the stop sentence.
      autoBetRef.current?.noteManualEdit()

      playEffect('chip')
      // The shared strip's own active disc (B31): the board no longer owns a
      // `.birdie-bar__chip`, and `--active` is always in the DOM on both
      // layouts — on a phone it is absolutely positioned inside the 3-chip
      // window, which still gives it a rect.
      const origin = chipBarRef.current
        ?.querySelector('.chip-panel__chip--active')
        ?.getBoundingClientRect()
      if (origin) {
        const id = `fly-${Date.now()}-${optionId}`
        setFlying((prev) =>
          prev.concat([
            {
              id,
              ...flightPath(origin, target),
              bg: selectedChip?.bg ?? '',
              border: selectedChip?.border ?? '',
              amount
            }
          ])
        )
        setTimeout(() => {
          setFlying((prev) => prev.filter((chip) => chip.id !== id))
        }, FLYING_CHIP_LIFETIME_MS)
      }

      // The pulse lands on the frame the flight ends (instantly under
      // `prefers-reduced-motion`, where no flight is drawn at all).
      if (receivingStartTimer.current) clearTimeout(receivingStartTimer.current)
      if (receivingClearTimer.current) clearTimeout(receivingClearTimer.current)
      setReceivingOptionId(null)
      receivingStartTimer.current = setTimeout(() => {
        setReceivingOptionId(optionId)
        receivingClearTimer.current = setTimeout(() => {
          setReceivingOptionId(null)
        }, RECEIVING_PULSE_MS)
      }, landingDelayMs())
    },
    [
      boardStateRef,
      boosterPool,
      dispatch,
      freeBetPool,
      limits.optionMax,
      marketIdByOption,
      onChipNotice,
      playEffect,
      selectedChip
    ]
  )
  const handleRemove = useCallback(
    (optionId: string) => {
      autoBetRef.current?.noteManualEdit()
      dispatch({ type: 'remove', optionId })
      playEffect('cancel')
    },
    [dispatch, playEffect]
  )
  /**
   * B28 — a locked tile answers the tap (owner: "when the current round is
   * running and trying to click on a betting option it should either show a
   * label or something"). The tile shakes; this is the sentence that goes with
   * it, on the board because `t` lives here.
   *
   * The string is an EXISTING key, already translated in all sixteen locales
   * (crash's bet slot raises the same one), so B28 adds no locale key at all.
   * Its en-soc entry is empty exactly as it is for crash, i.e. en-soc falls
   * back to English — correct here, the sentence carries no sweepstakes
   * vocabulary.
   *
   * Deliberately SILENT. The board's refusal effect is what a chip notice
   * plays, and a player tapping a tile through a running round would blip it
   * over and over; the audio decision belongs to B23.
   *
   * Takes no argument even though the tile offers the option id: the sentence
   * is the same whichever option was tapped, and `showNotice` re-raises on the
   * next frame, so a second tap replays the pill on its own.
   */
  const handleLockedPress = useCallback(() => {
    showNotice(t('Waiting for next round'), 'info')
  }, [showNotice, t])
  useEffect(
    () => () => {
      if (receivingStartTimer.current) clearTimeout(receivingStartTimer.current)
      if (receivingClearTimer.current) clearTimeout(receivingClearTimer.current)
    },
    []
  )

  // ── The live per-putt score ────────────────────────────────────────────
  // The rows arrive on the shared broadcast and are diverted into their own
  // slice by `routeBirdieProgressRow` (socket handler + hydration mapper), so
  // they never reach `raceEvents`. All the host does is claim the round and
  // hand the entries to `boardPhase`, which applies the widget-side hold.
  const currentRaceId = currentRace?.uuid
  const puttsActions = useBirdiePuttsActions()
  const puttEntries = useBirdiePutts(currentRaceId)
  // B36: the drawn type, once the `round_type` row (or the last putt row) has revealed it. It is
  // held like a ring — the row is armed on the last putt's cup tell, and the board must not light
  // the tile before the viewer's own picture has lit the cup (owner 2026-09-22).
  const rawRevealedType = useBirdieRoundType(currentRaceId)
  const revealedAtMs = useBirdieRoundTypeAtMs(currentRaceId)
  useEffect(() => {
    if (currentRaceId === undefined) return
    puttsActions.setCurrentRace(currentRaceId)
  }, [currentRaceId, puttsActions])

  // ── Phase ──────────────────────────────────────────────────────────────
  const [nowMs, setNowMs] = useState(() => Date.now())
  // The ring's `performance.now()`, read on the 1 Hz tick only: the ring eases
  // toward each tick's drain over a fixed CSS transition, so a drain read at
  // every render restarted that ease on each extra render (a bet's chips, its
  // confirmation, the feed) and the ring ran slow, quick, slow, quick on a
  // round with bets (owner 10-29). A legacy round reads `nowMs`, which already
  // moves only on the tick.
  const [ringPerfMs, setRingPerfMs] = useState(perfNow)
  const revealedType = roundTypeVisible({
    roundType: rawRevealedType,
    revealedAtMs,
    nowMs,
    holdMs: BIRDIE_RESULT_HOLD_MS
  })
  // The 1 Hz tick would land the reveal anywhere inside the second after its hold; one timer,
  // armed on the arrival stamp, puts it ON the hold — the same treatment the settlement gets.
  useEffect(() => {
    if (revealedAtMs === undefined) return undefined
    const delay = revealedAtMs + BIRDIE_RESULT_HOLD_MS - Date.now()
    if (delay <= 0) return undefined
    const timer = setTimeout(() => {
      setNowMs(Date.now())
    }, delay)
    return () => {
      clearTimeout(timer)
    }
  }, [revealedAtMs])
  const [betsSubmitted, setBetsSubmitted] = useState(false)
  // The reveal hold counts from when THIS client saw `race_settled`, on its own
  // clock (boardPhase.settledSeenAtMs) - never the server stamp minus Date.now().
  // Recorded once per round, on the render the event first appears.
  const settledSeenRef = useRef<{ raceId?: string; atMs?: number }>({})
  const settledEventForClock = raceEvents.find(isRaceSettledEvent)
  if (settledEventForClock !== undefined && settledSeenRef.current.raceId !== currentRace?.uuid) {
    settledSeenRef.current = { raceId: currentRace?.uuid, atMs: Date.now() }
  }
  const settledSeenAtMs =
    settledEventForClock !== undefined && settledSeenRef.current.raceId === currentRace?.uuid
      ? settledSeenRef.current.atMs
      : undefined
  // Hoisted out of the `boardPhase` call because the capsule's between-rounds
  // ring (B16) counts against the SAME window — two reads of `game.settledWindow`
  // could drift apart, and the number under the avatar would then disagree with
  // the phase that draws it.
  const settledWindowSec = game.settledWindow || BET_CONFIG.DEFAULT_SETTLED_WINDOW_SEC
  // A hard round's drain runs to the board's lock, then over X (DESIGN §6),
  // read at the board's own tick. Null on every other round: today's drain.
  const hardClock = hardCloseClock(currentRace, races[1])
  const hardPerfNow = perfNow()
  const hardCount = hardClock ? hardCountdown(hardClock, hardPerfNow) : null
  const phase = boardPhase({
    hard: hardCount,
    raceEvents,
    nowMs,
    bettingOpenWindowSec: game.bettingOpenWindow || BET_CONFIG.DEFAULT_BETTING_OPEN_WINDOW_SEC,
    bettingClosingWindowSec:
      game.bettingClosingWindow || BET_CONFIG.DEFAULT_BETTING_CLOSING_WINDOW_SEC,
    settledWindowSec,
    betsSubmitted,
    putts: puttEntries,
    settledSeenAtMs,
    hardNowMs: ringPerfMs,
    ringLeadMs: BIRDIE_RING_EASE_MS
  })
  // The live putts rode the same 1 Hz tick, so a ring landed anywhere inside the second after
  // its hold — up to a second of pure luck on top of the hold (owner 2026-09-23). One timer on
  // the next held putt puts it ON the hold, like the round type and the settlement; re-armed on
  // every tick and every arrival so a second held putt is caught after the first reveals.
  useEffect(() => {
    const delay = nextPuttRevealDelayMs({
      putts: puttEntries,
      holdMs: BIRDIE_RESULT_HOLD_MS,
      nowMs: Date.now()
    })
    if (delay === null) return undefined
    const timer = setTimeout(() => {
      setNowMs(Date.now())
    }, delay)
    return () => {
      clearTimeout(timer)
    }
  }, [puttEntries, nowMs])
  useEffect(() => {
    const timer = setInterval(() => {
      setNowMs(Date.now())
      setRingPerfMs(perfNow())
    }, TICK_MS)
    return () => {
      clearInterval(timer)
    }
  }, [])

  // B11 item 7 / review 10: the 1 Hz interval above also drove the settlement
  // reveal, so the rings, the winner bloom and the total-win banner landed
  // anywhere between the hold and the hold plus a second — purely by where in
  // the tick `race_settled` happened to arrive. One extra timer, armed on the
  // settle time and cleared when it changes, puts the reveal ON the hold; the
  // interval keeps running for the countdown and the live putts.
  const settledAtMs = phase.settledAtMs
  useEffect(() => {
    const delay = settlementRevealDelayMs({ settledAtMs, nowMs: Date.now() })
    if (delay === null) return undefined
    const timer = setTimeout(() => {
      setNowMs(Date.now())
    }, delay)
    return () => {
      clearTimeout(timer)
    }
  }, [settledAtMs])

  // Once the settled pattern is on screen the live entries have done their job
  // and a late row must not resurrect a ring. `clearRace` is a no-op when there
  // is nothing to drop, so this cannot loop.
  useEffect(() => {
    if (!phase.settlementVisible || currentRaceId === undefined) return
    puttsActions.clearRace(currentRaceId)
  }, [currentRaceId, phase.settlementVisible, puttsActions])

  // ── Settlement ─────────────────────────────────────────────────────────
  const settledEvent = useMemo(() => raceEvents.find(isRaceSettledEvent), [raceEvents])
  const settlement = useMemo(
    () =>
      phase.settlementVisible && settledEvent
        ? settlementView(settledEvent.payload, game.marbles)
        : undefined,
    [game.marbles, phase.settlementVisible, settledEvent]
  )
  // The card's / strip's ring row. One shape, two sources: the live per-putt
  // marks while the clip plays, then the settled pattern, which WINS and
  // overwrites them (`boardPhase` already empties `livePutts` at that point).
  // A ring that was already on screen gets delay 0 so it never re-animates —
  // its slot keeps its DOM node, only `--empty` → `--filled` starts the
  // scale-in. Because the host ticks at 1 Hz a ring can appear up to 1 s after
  // its hold elapsed; that is inside the ±300 ms budget's slack and NOT a
  // reason for a faster ticker (S1 measures the real video-vs-card offset).
  const livePutts = phase.livePutts
  const shownRingsRef = useRef(0)
  const rings = useMemo<BirdieRingsView | undefined>(() => {
    if (phase.settlementVisible) {
      if (!settlement || settlement.marks.length === 0) return undefined
      // B11 item 9 / review 15: the row was labelled with the RAW pattern key,
      // so a screen reader read out "H M H". It is the same option label the
      // winning tile carries, behind a literal translate call on the Result key.
      const pattern = settlement.pattern
      return {
        marks: settlement.marks,
        delaysMs: settlement.delaysMs,
        ...(pattern === undefined
          ? {}
          : {
              label: `${t('Result')}: ${getTranslatedMarbleColor(pattern, t as unknown as TFunction)}`
            })
      }
    }
    if (livePutts.length === 0) return undefined
    return {
      marks: livePutts,
      delaysMs: ringDelaysForLive(livePutts.length, shownRingsRef.current, RING_STAGGER_MS),
      // B34: two IN and the third under way — the pending slot burns.
      hot: liveStatus(tiles, livePutts).streak
    }
  }, [livePutts, phase.settlementVisible, settlement, t, tiles])
  useEffect(() => {
    shownRingsRef.current = phase.settlementVisible ? 0 : livePutts.length
  }, [livePutts.length, phase.settlementVisible])

  // ── Live ticket status + the putt callout (B34) ────────────────────────
  // Both ride `phase.livePutts`, which is empty until the first putt is on
  // screen and empty again once the settlement is visible, so the settled
  // classes and the win pill never compete with them.
  const live = useMemo(
    () => liveStatus(tiles, livePutts, revealedType),
    [livePutts, revealedType, tiles]
  )
  // B41 — publish what the board is drawing, so the social feed can settle ball
  // by ball WITHOUT a second copy of the hold. `livePutts` is already held and
  // already emptied at settlement, so a reader of this can never lead the
  // video; the store drops the revealed type while nothing is on screen.
  useBirdiePublishLiveBoard({
    ...(currentRaceId === undefined ? {} : { raceId: currentRaceId }),
    marks: livePutts,
    ...(revealedType === undefined ? {} : { revealedType })
  })
  // B40 item 8: the two revealed types the board has a look for. `plain` is the
  // common case and draws nothing — weather that fired every round would say
  // nothing at all — and the reveal is dropped the moment the settlement is on
  // screen, which is what keeps the pass a LIVE beat rather than a second
  // announcement of a result the tiles have already made.
  // B40 — the board tips into the scene for the round and lies flat again the
  // moment the result is on screen (`boardSeated`). The desktop arc only: the
  // phone's board is a column in the flow under the video, where there is no
  // scene to be seated in.
  const boardIsSeated = boardSeated({
    phase: phase.phase,
    latestType,
    settlementVisible: phase.settlementVisible,
    hardLocked: phase.hardLocked
  })
  const boardWeather: 'frost' | 'fire' | undefined =
    !phase.settlementVisible && (revealedType === 'frost' || revealedType === 'fire')
      ? revealedType
      : undefined
  const callout = useBirdieCallout({
    marks: livePutts,
    raceId: currentRaceId,
    settlementVisible: phase.settlementVisible
  })
  const calloutCopy = useMemo(
    () => (callout ? birdieCalloutCopy(callout, t) : undefined),
    [callout, t]
  )

  // ── The tee-off countdown (B37) ────────────────────────────────────────
  // The slot is empty from `race_started` until the first putt is drawn — the
  // engine's reveal delay, five seconds of nothing on staging. The count fills
  // it and hands over: `firstPuttSeen` is the SAME `livePutts` the callout
  // reads, so the beat stops on the render the pill starts saying "Putt 1 of 3"
  // (B37, `teeOff.ts`). `revealDelayS` is the engine's own number off the
  // round's committed board; a round without one gets no countdown at all.
  const startedAtMs = useMemo(() => {
    const startedEvent = raceEvents.find((event) => event.eventType === 'race_started')
    return startedEvent === undefined ? undefined : new Date(startedEvent.eventTimestamp).getTime()
  }, [raceEvents])
  // A hard round with the short hold counts the whole of X instead: the
  // tee-off IS the lock (owner 10-29), up from the moment the capsule slides
  // to the corner, on this page's arrival clock, to its estimate of K, the cut
  // the first putt follows by R (DESIGN §6). With the legacy hold the putts
  // still wait out the engine's reveal delay, so today's count stays.
  const hardTeeOff =
    hardClock !== null && hardClock.close.holdS !== null && hardClock.lockedAt !== undefined
  const teeOff = hardTeeOff
    ? teeOffCountdown({
        startedAtMs: hardClock.lockedAt,
        revealDelayS: hardClock.close.lockLeadS,
        beats: Math.ceil(hardClock.close.lockLeadS),
        nowMs: hardPerfNow,
        firstPuttSeen: livePutts.length > 0,
        settled: phase.phase === 'settled' || latestType === 'race_cancelled'
      })
    : teeOffCountdown({
        ...(startedAtMs === undefined ? {} : { startedAtMs }),
        ...(roundOdds === undefined ? {} : { revealDelayS: roundOdds.revealDelayS }),
        nowMs,
        firstPuttSeen: livePutts.length > 0,
        settled: phase.phase === 'settled' || latestType === 'race_cancelled'
      })

  // ── The capsule (B16) ──────────────────────────────────────────────────
  // The host owns the reduced-motion flag rather than the capsule, because
  // `cardBoxPx(layout)` is set HERE and the two must agree on one layout. Read
  // after hydration: the capsule is on screen from the first frame, so reading
  // the media query during render would make the server's markup differ from
  // the client's. The cost is one frame of nothing — the stylesheet's own
  // `prefers-reduced-motion` block already kills every animation it declares,
  // and none of the JS-side gates can complete inside a frame either.
  const [reducedMotion, setReducedMotion] = useState(false)
  useEffect(() => {
    setReducedMotion(prefersReducedMotion())
  }, [])

  const capsule = capsuleState({
    phase: phase.phase,
    settlementVisible: phase.settlementVisible,
    reducedMotion,
    hardLocked: phase.hardLocked
  })
  // The countdown moved onto the capsule (owner: "the countdown etc. we show to
  // the right … could be removed … maybe part of the capsule with the golfer
  // details"), so it takes `boardPhase`'s own numbers — the ring around the
  // avatar and the arc hairline are literally the same `drain`.
  const ringView = avatarRing({
    phase: phase.phase,
    drain: phase.drain,
    secondsLeft: phase.secondsLeft,
    ...(phase.settledAtMs === undefined ? {} : { settledAtMs: phase.settledAtMs }),
    settledWindowSec,
    nowMs,
    hardLocked: phase.hardLocked,
    leadMs: BIRDIE_RING_EASE_MS
  })
  // B16b dropped the settled headline from the UI — owner: "maybe not that 2 of
  // 3 in, not needed, the visuals should be enough". The settled capsule now
  // says it with the three marks, a gold edge and one sweep. The KEY and its
  // sixteen translations stay in the locale files, held there by
  // `birdieFinalScoreCopy` in `birdieCopy.ts`, which is the only place the
  // literal-only extractor can see it: deleting the call site would have
  // `i18n:cleanup` remove all sixteen the next time it runs.

  const winnerIds = useMemo(() => {
    const ids = new Set<string>()
    if (settlement?.countOptionId !== undefined) ids.add(settlement.countOptionId)
    if (settlement?.exactOptionId !== undefined) ids.add(settlement.exactOptionId)
    // The third market lights like the other two. A plain round adds nothing: it is a real settled
    // type, and the board never carried a tile for it.
    const roundTypeId = roundTypeWinnerId(settlement?.roundType, tiles)
    if (roundTypeId !== undefined) ids.add(roundTypeId)
    return ids
  }, [settlement, tiles])
  // `settleBetsForRace` flips bet status client-side in the same socket callback
  // that appends `race_settled`, up to the hold ahead of the rings, the winner
  // pill and the total. The tile's own win/loss dressing waits for the hold
  // like its neighbours (`settlement`, `winnerIds`, `totalWin`) - a lit tile is
  // the result, and the result is not shown before the picture has caught up.
  const betByOptionId = useMemo(() => {
    const out = new Map<string, { won: boolean; lost: boolean; payout: number; tier: WinTier }>()
    for (const bet of currentBets) {
      const optionId = bet.betDetails.selectedItems[0]
      if (!optionId) continue
      out.set(optionId, {
        won: phase.settlementVisible && bet.status === 'won',
        lost: phase.settlementVisible && bet.status === 'lost',
        payout: bet.potentialPayout,
        tier: tierForBet(bet)
      })
    }
    return out
  }, [currentBets, phase.settlementVisible])
  const totalWin = useMemo(
    () =>
      phase.settlementVisible
        ? currentBets
            .filter((bet) => bet.status === 'won')
            .reduce((sum, bet) => sum + bet.potentialPayout, 0)
        : 0,
    [currentBets, phase.settlementVisible]
  )
  const topWinTier = useMemo<WinTier>(() => {
    let best: WinTier = 'win'
    for (const bet of currentBets) {
      const tier = tierForBet(bet)
      if (tier === 'mega') return 'mega'
      if (tier === 'big') best = 'big'
    }
    return best
  }, [currentBets])

  // ── Birdie's cues (B23) ───────────────────────────────────────
  // Every cue rides a state the board already derived for its picture — the
  // phase, the tee-off beat, the drawn rings, the visible settlement —
  // so a sound can never lead the video by more than the picture does. The rule
  // is `birdieCues` (pure, table-tested); this is the scheduler. The Howls are
  // built once here, on mount, so the first cue of the round is not also a load.
  useEffect(() => {
    preloadBirdieEffects()
  }, [])
  useBirdieCues(
    {
      ...(currentRaceId === undefined ? {} : { raceId: currentRaceId }),
      phase: phase.phase,
      ...(teeOff === undefined ? {} : { teeOffBeat: teeOff }),
      liveMarks: livePutts,
      settlementVisible: phase.settlementVisible,
      hasBets: currentBets.length > 0,
      won: totalWin > 0,
      lightningRevealed: boostedIds.size > 0,
      // the card's make chance decides birdie vs eagle on a clean sweep (`roundOdds` is declared above)
      ...(roundOdds?.card.makeRatePpm === undefined
        ? {}
        : { makeRate: roundOdds.card.makeRatePpm / 1_000_000 })
    },
    playEffect
  )

  // The shared panel's big-win layering (ChipPlacementPanel.tsx:1154-1167): the
  // base "you won" notification still comes from ShowRaceBannerContainer; these
  // chimes land just before the total-win banner. Birdie's own chime, not the
  // shared `bonus`, which is the level-up sound (owner 2026-09-21).
  useEffect(() => {
    if (!phase.settlementVisible || totalWin <= 0 || topWinTier === 'win') return undefined
    const lead = Math.max(0, TOTAL_WIN_DELAY_MS - WIN_CHIME_LEAD_MS)
    const first = setTimeout(() => {
      playEffect('birdieBigWin')
    }, lead)
    let second: ReturnType<typeof setTimeout> | null = null
    if (topWinTier === 'mega') {
      second = setTimeout(() => {
        playEffect('birdieBigWin')
      }, lead + MEGA_CHIME_GAP_MS)
    }
    return () => {
      clearTimeout(first)
      if (second) clearTimeout(second)
    }
  }, [phase.settlementVisible, playEffect, topWinTier, totalWin])

  // ── Submission lifecycle (the shared pure helpers, board-local state) ───
  const submissionStartedRef = useRef(false)
  const submittedRoundIdRef = useRef<string | undefined>(undefined)
  const observedBettingOpenRef = useRef(false)
  const submitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** The close backstop (`submitBackstop.ts`), armed on race_ready and cleared by the next one. */
  const backstopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const staleHeldRoundIdRef = useRef<string | undefined>(undefined)
  const betsActuallySentRef = useRef(false)
  const currentRoundIdRef = useRef<string | undefined>(currentRace?.uuid)
  currentRoundIdRef.current = currentRace?.uuid
  /** betId → optionId for this round's submission, so an outcome finds its chip. */
  const trackedBetsRef = useRef<TrackedBets>({})

  const triggerSubmission = useCallback(
    async (options?: { timerArmedAt?: number; timerDelayMs?: number; hardLock?: BetLockSent }) => {
      if (submissionStartedRef.current) return
      const chipsNow = boardStateRef.current.chips
      if (Object.keys(chipsNow).length === 0) {
        // Nothing to send - but on the TIMER path this is the close of the window,
        // and a chip placed after it would only reach the `race_started` fallback
        // at close + race_starting_before, which is exactly Go's cutoff: silently
        // refused. Lock the tiles now for an empty board too. The fallback path
        // carries no `timerArmedAt` and stays a no-op. A hard round's lock is the
        // close of the window too: the board locks empty or not.
        if (options?.timerArmedAt !== undefined || options?.hardLock) setBetsSubmitted(true)
        return
      }
      if (
        isFrozenSubmission({
          timerArmedAt: options?.timerArmedAt,
          timerDelayMs: options?.timerDelayMs,
          now: Date.now()
        })
      ) {
        staleHeldRoundIdRef.current = currentRoundIdRef.current
        showNotice(t('Bets held for the next round'), 'info')
        return
      }
      if (!player.balanceLoaded) return
      // Only the CASH-funded chips are checked against the wallet: a free bet is
      // paid by the reward, and counting it here would clear the chip and skip
      // the /bet at a low balance — the shared panel's own reasoning
      // (`ChipPlacementPanel.tsx:1979-1983`).
      const claimsNow = inventoryRef.current
      if (chargeableStake(chipsNow, claimsNow.freeBets) > player.balance.amount) {
        dispatch({ type: 'clear' })
        showNotice(t('Insufficient balance'), 'error')
        return
      }

      submissionStartedRef.current = true
      submittedRoundIdRef.current = currentRoundIdRef.current
      setBetsSubmitted(true)
      dispatch({ type: 'snapshot' })

      // The two maps the submit hook attaches to the payload: the item id rides
      // on `betDetails.inventoryItemIds` and a free bet also sets `isFreeBet`,
      // so the optimistic debit does not take a stake the reward paid. A hard
      // round's send waits for its channel's join until K − 0.5 s, no longer.
      const hardLock = options?.hardLock
      const result = hardLock
        ? await submitAllBets(chipsNow, claimsNow.boosters, claimsNow.freeBets, {
            joinDeadline: stageTwoAt(hardLock.at, hardLock.lockLeadS).joinDeadline
          })
        : await submitAllBets(chipsNow, claimsNow.boosters, claimsNow.freeBets)
      if (hardLock) setHardSentBetIds(result.sent)
      const outcome = classifySubmitOutcome({
        sent: result.sent.length,
        aborted: result.aborted,
        attemptedChips: Object.keys(chipsNow).length
      })
      betsActuallySentRef.current = outcome.betsActuallySent
      autoBetRef.current?.noteWireResult(currentRoundIdRef.current, outcome.betsActuallySent)
      if (outcome.unlockForRetry) {
        submissionStartedRef.current = false
        // A hard round has nothing after its lock to retry from: its tiles stay
        // locked and the chips carry to the next round.
        if (!hardLock) setBetsSubmitted(false)
        showNotice(t('Couldn’t place — holding for next round'), 'warning')
        return
      }
      // Remember which chip each bet on the wire belongs to, so the watcher
      // below can strip the ones the server refuses. The submit hook returns
      // ids only, so they are resolved against the store it just wrote to.
      const store = useBetStore.getState()
      trackedBetsRef.current = trackSubmittedBets({
        sentIds: result.sent,
        store: { unacceptedBets: store.unacceptedBets, betsById: store.bets.byId },
        existing: trackedBetsRef.current
      })
    },
    [
      boardStateRef,
      dispatch,
      player.balance.amount,
      player.balanceLoaded,
      showNotice,
      submitAllBets,
      t
    ]
  )
  const triggerSubmissionRef = useRef(triggerSubmission)
  triggerSubmissionRef.current = triggerSubmission

  // B4 — the reset `race_ready` should have done never ran, so last round's
  // BOARD is still here alongside its flags. Mirror the race_ready decision
  // before unlocking anything: unlocking on its own would leave the send to
  // submit last round's chips as new bets on this round
  // (ChipPlacementPanel.tsx guards the same thing). Run at race_starting, and at
  // a hard round's lock before it sends.
  const recoverMissedReadyReset = () => {
    if (
      shouldRecoverMissedRoundReset({
        submissionStarted: submissionStartedRef.current,
        submittedRoundId: submittedRoundIdRef.current,
        currentRoundId: currentRoundIdRef.current
      })
    ) {
      const hadSuccessfulSubmission = betsActuallySentRef.current
      datadogLogs.logger.warn('birdie_missed_race_ready_recovered', {
        user_event: {
          roundId: currentRoundIdRef.current,
          submittedRoundId: submittedRoundIdRef.current,
          hadSuccessfulSubmission
        }
      })
      dispatch({
        type: 'roundReset',
        keep: missedReadyRecovery({ hadSuccessfulSubmission }).keepChips
      })
      submissionStartedRef.current = false
      submittedRoundIdRef.current = undefined
      betsActuallySentRef.current = false
      trackedBetsRef.current = {}
      setBetsSubmitted(false)
    }
  }
  const recoverMissedReadyResetRef = useRef(recoverMissedReadyReset)
  recoverMissedReadyResetRef.current = recoverMissedReadyReset

  // The hard close (rev 4, DESIGN §6): on a round whose race_ready says
  // bet_close "hard", the board locks at the FIRST of race_starting's arrival
  // and its own countdown, empty or not, and sends every chip at once. No
  // 1.5 s timer, no close backstop, no race_started fallback. The Clubhouse
  // table locks by the same two signals (`world/real/rounds/venueTable.ts`).
  // Every other round: `betLock.hard` false, and the board is today's.
  const betLock = useBetLock({
    race: currentRace,
    previousRace: races[1],
    onLock(sent) {
      if (!observedBettingOpenRef.current) {
        setBetsSubmitted(true)
        return
      }
      recoverMissedReadyResetRef.current()
      if (sent.frozen) {
        // Today's frozen-tab check, on the countdown timer: hold for the next round.
        staleHeldRoundIdRef.current = currentRoundIdRef.current
        setBetsSubmitted(true)
        showNotice(t('Bets held for the next round'), 'info')
        return
      }
      void triggerSubmissionRef.current({ hardLock: sent })
    }
  })
  const [hardSentBetIds, setHardSentBetIds] = useState<string[]>([])
  const stageTwo = useStageTwo({
    lockedAt: betLock.lockedAt,
    lockLeadS: betLock.lockLeadS,
    betIds: hardSentBetIds
  })

  useEffect(() => {
    if (latestType === 'race_ready') {
      observedBettingOpenRef.current = true
      staleHeldRoundIdRef.current = undefined
      // `autoBet: false` even with a B32 rule armed: Birdie's auto bet places a
      // FRESH plan against this round's prices (below, after this reset) and
      // never rebets last round's board.
      const action = roundTransitionAction({
        autoBet: false,
        hadSuccessfulSubmission: betsActuallySentRef.current
      })
      submissionStartedRef.current = false
      submittedRoundIdRef.current = undefined
      betsActuallySentRef.current = false
      trackedBetsRef.current = {}
      setBetsSubmitted(false)
      setHardSentBetIds((prev) => (prev.length === 0 ? prev : []))
      dispatch({ type: 'roundReset', keep: action === 'carryForward' })
      // The close backstop (B27 item 10). `race_starting` lands late on ~4 % of
      // rounds and the timer below is armed on its ARRIVAL, so those rounds
      // submitted past Go's cutoff and settled empty. This one is armed from the
      // engine's own stamps and fires at close + the timer + a grace; it is a
      // no-op once the timer path has submitted. It is cleared by the NEXT
      // race_ready and on unmount — never by the race_starting transition, which
      // is exactly the moment it exists to outlive.
      if (backstopTimerRef.current) clearTimeout(backstopTimerRef.current)
      backstopTimerRef.current = null
      // A hard round has no backstop: its board locks on its own countdown.
      if (betLock.hard) return undefined
      const backstopMs = submitBackstopDelayMs({
        readyTimestamp: raceEvents.find((event) => event.eventType === 'race_ready')
          ?.eventTimestamp,
        scheduledCloseAt: roundOdds?.scheduledCloseAt
      })
      if (backstopMs !== undefined) {
        const backstopArmedAt = Date.now()
        backstopTimerRef.current = setTimeout(() => {
          backstopTimerRef.current = null
          void triggerSubmissionRef.current({
            timerArmedAt: backstopArmedAt,
            timerDelayMs: backstopMs
          })
        }, backstopMs)
      }
      return undefined
    }
    if (latestType === 'race_starting') {
      if (!observedBettingOpenRef.current) {
        // A client mounted during the closing phase never saw `race_ready`, so
        // nothing is armed for it — but the tiles were open for the whole close,
        // and a chip placed now reached no submit path and was carried,
        // unannounced, to the next round (B27 item 23). Lock the board for the
        // rest of this round; the next `race_ready` reset unlocks it.
        setBetsSubmitted(true)
        return undefined
      }
      recoverMissedReadyResetRef.current()
      // A hard round has no 1.5 s timer: it sends at its lock (`useBetLock` above).
      if (betLock.hard) return undefined
      const armedAt = Date.now()
      submitTimerRef.current = setTimeout(() => {
        void triggerSubmissionRef.current({ timerArmedAt: armedAt })
      }, SUBMIT_TIMER_MS)
      return () => {
        if (submitTimerRef.current) clearTimeout(submitTimerRef.current)
      }
    }
    if (latestType === 'race_started') {
      if (!observedBettingOpenRef.current) return undefined
      if (staleHeldRoundIdRef.current === currentRoundIdRef.current) return undefined
      // A hard round has no race_started fallback: a board that did not lock sends nothing.
      if (betLock.hard) return undefined
      void triggerSubmissionRef.current()
    }
    return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the phase only (the B3 latest-ref pattern)
  }, [latestType])

  useEffect(
    () => () => {
      if (backstopTimerRef.current) clearTimeout(backstopTimerRef.current)
    },
    []
  )

  // Restore after a refresh: rebuild the board from the server's bets.
  //
  // Keyed on the ROUND and on the stable `dispatch`/`stateRef`, never on the
  // `board` object — `useBirdieChips` returns a fresh one every render, which
  // ran this effect on every commit. `restorableChips` drops anything that is
  // not this round's, so the commit where the round rolls over (where
  // `useGetCurrentBets` still holds the previous round's list) restores
  // nothing. `submittedRoundIdRef` is set too, so a missed `race_ready` can
  // still be recovered from a restored board.
  useEffect(() => {
    if (currentRaceId === undefined) return
    // Once this session has submitted for this round the BOARD is authoritative:
    // a bet the server refused (or never registered) can still be sitting in the
    // optimistic store until the global settle purge, and the reconcile above
    // has just stripped its chip — restoring would put it straight back.
    if (submissionStartedRef.current && submittedRoundIdRef.current === currentRaceId) return
    if (Object.keys(boardStateRef.current.chips).length > 0) return
    const restored = restorableChips({
      bets: currentBets,
      roundId: currentRaceId,
      marketIdByOption
    })
    if (Object.keys(restored).length === 0) return
    dispatch({ type: 'restore', chips: restored })
    submissionStartedRef.current = true
    submittedRoundIdRef.current = currentRaceId
    betsActuallySentRef.current = true
    setBetsSubmitted(true)
  }, [boardStateRef, currentBets, currentRaceId, dispatch, marketIdByOption])

  // ── Reject reconcile ───────────────────────────────────────────────────
  // `useChipPlacementBetSubmit` is explicit that the caller strips the chips of
  // bets that disappear from the store: `bet:rejected` purges the bet and
  // refunds the stake globally, but nothing here would stop drawing the chip —
  // it would sit on the locked tile all race, counted in the Total Bet and
  // Potential Win pills, contradicting the rejection toast the player just saw.
  // Mounted once; the tracking ref keeps it current (ChipPlacementPanel.tsx:2124).
  useEffect(
    () =>
      useBetStore.subscribe((state) => {
        if (Object.keys(trackedBetsRef.current).length === 0) return
        // A hard round's late answer (`NotInTime`): no bet, the stake is being
        // rolled back. Said once, as the tracked bet resolves.
        const late = state.lastRejection
        const notInTime =
          late?.detail === RejectionDetails.NotInTime && late.betId in trackedBetsRef.current
        const resolved = resolveTrackedBets({
          tracked: trackedBetsRef.current,
          store: { unacceptedBets: state.unacceptedBets, betsById: state.bets.byId }
        })
        trackedBetsRef.current = resolved.tracked
        if (resolved.rejectedOptionIds.length === 0) return
        boardDispatchRef.current({ type: 'rejected', optionIds: resolved.rejectedOptionIds })
        if (notInTime) showNoticeRef.current(notInTimeTextRef.current, 'warning')
      }),
    []
  )

  // The settle-time safety net: a bet still unacknowledged now was never
  // registered (no `bet:rejected` ever arrived for it). The refund is the
  // global race_settled handler's job; this only stops the board drawing it.
  useEffect(() => {
    if (latestType !== 'race_settled') return
    if (submittedRoundIdRef.current !== currentRoundIdRef.current) return
    const tracked = trackedBetsRef.current
    trackedBetsRef.current = {}
    const stuck = stuckAtSettlement({
      tracked,
      unacceptedBets: useBetStore.getState().unacceptedBets
    })
    if (stuck.betIds.length === 0) return
    datadogLogs.logger.warn('birdie_bet_silent_failure', {
      user_event: {
        roundId: currentRoundIdRef.current,
        betIds: stuck.betIds,
        optionIds: stuck.optionIds
      }
    })
    boardDispatchRef.current({ type: 'rejected', optionIds: stuck.optionIds })
    // The chips are about to vanish off the board. Say why — silently removing
    // a stake the player watched themselves place is worse than the failure.
    // Two literal keys rather than i18next plurals: the locale files are keyed
    // by the English sentence (birdieCopy.ts, `birdieChipsClampedCopy`).
    const count = stuck.optionIds.length
    showNotice(
      count === 1
        ? t('{{count}} chip didn’t go through', { count })
        : t('{{count}} chips didn’t go through', { count }),
      'error'
    )
  }, [latestType, showNotice, t])

  // ── Stats pills contract ───────────────────────────────────────────────
  // Reported when the NUMBERS change, never when the object does. `totals` is a
  // memo over `state.chips` and the price maps, and those maps are rebuilt
  // whenever `game` arrives as a fresh object — which is exactly what the
  // parent's own `setChipStats` causes. Keyed on identity, the effect fed that
  // back: stats up, page re-renders, new `game`, new maps, new `totals`, stats
  // up again, until React gave up with "Maximum update depth exceeded"
  // (reproduced in dev on 2026-09-18 and watched go quiet with this).
  //
  // Three numbers is the whole payload, so value equality is the honest test of
  // whether the parent has anything to hear about.
  const lastStatsRef = useRef<BirdieBoardStats | null>(null)
  useEffect(() => {
    const next = board.totals
    const previous = lastStatsRef.current
    if (
      previous !== null &&
      previous.totalStaked === next.totalStaked &&
      previous.totalPotentialWin === next.totalPotentialWin &&
      previous.chipCount === next.chipCount
    ) {
      return
    }
    lastStatsRef.current = next
    onStatsChange?.(next)
  }, [board.totals, onStatsChange])

  // ── Board telemetry (Task 5.4's decision function) ─────────────────────
  // A51/A82 — RUM is off, so a board that silently shows the previous round
  // leaves no trace. `birdie_board_missing` is unsampled; `birdie_board_rendered`
  // is sampled 1-in-50 client-side and UNSAMPLED on a paytable-hash mismatch,
  // which is why the hash is really recomputed here (Task 5.4's paytableHash).
  const loggedRoundRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    const roundId = currentRace?.uuid
    if (roundId === undefined) return undefined
    const readyEvent = raceEvents.find((event) => event.eventType === 'race_ready')
    if (!readyEvent) return undefined
    const readyAgeMs = Date.now() - new Date(readyEvent.eventTimestamp).getTime()
    let cancelled = false
    const run = async (): Promise<void> => {
      let hashMatch: boolean | undefined
      if (roundOdds) {
        const inputs = paytableInputsFromOdds(roundOdds, game.marbles)
        hashMatch = inputs
          ? (await paytableHash(roundOdds.rtpPpm, inputs.weights, inputs.multipliers)) ===
            roundOdds.paytableHash
          : false
      }
      if (cancelled) return
      const decision = boardTelemetryDecision({
        isBirdie: true,
        hasReady: true,
        hasStarted: raceEvents.some((event) => event.eventType === 'race_started'),
        hasOdds: roundOdds !== undefined,
        readyAgeMs,
        loggedRoundId: loggedRoundRef.current,
        roundId,
        random: Math.random(),
        hashMatch
      })
      if (decision === 'none') return
      loggedRoundRef.current = roundId
      if (decision === 'missing') {
        datadogLogs.logger.warn('birdie_board_missing', {
          user_event: { roundId, gameId: game.id, readyAgeMs }
        })
      } else if (decision === 'rendered') {
        datadogLogs.logger.info('birdie_board_rendered', {
          user_event: {
            roundId,
            gameId: game.id,
            paytableHash: roundOdds?.paytableHash,
            hashMatch,
            catalogueVersion: roundOdds?.catalogueVersion,
            makeRatePpm: roundOdds?.card.makeRatePpm
          }
        })
      }
    }
    // No odds yet: wait out the grace window before calling the board missing.
    const timer = roundOdds
      ? undefined
      : setTimeout(
          () => {
            void run()
          },
          Math.max(0, BOARD_MISSING_GRACE_MS - readyAgeMs)
        )
    if (roundOdds) void run()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [currentRace?.uuid, game.id, game.marbles, raceEvents, roundOdds])

  // ── "Betting unavailable this round" (A88, Task 5.6) ───────────────────
  // The hook latches the flag per round and logs `birdie_round_not_open`
  // once; the board only renders it. Do NOT call shouldFlagRoundNotOpen here.
  const { flagged: roundNotOpen } = useRoundNotOpen(game)

  const persistentNotice = useMemo(() => {
    // The TRANSIENT notice wins while it is alive; the persistent one is the
    // resting state underneath it. Order matters, and B28 is why: the
    // `roundNotOpen` flag LATCHES for the rest of the round (`useRoundNotOpen`
    // clears it on the round id, not on the phase), so it is still raised
    // through `race_started` and settlement — phases whose tiles are priced,
    // rendered and, since B28, tappable. Answering a tap with `showNotice` and
    // then dropping it on the floor is exactly the silence B28 exists to end.
    if (notice) return notice
    // Stage 2 of a hard round (DESIGN §6): the lock's send, until its answers come.
    if (stageTwo === 'placing') {
      return { message: `${t('Locked')} · ${t('Placing bet...')}`, tone: 'info' as const }
    }
    if (stageTwo === 'confirming') {
      return { message: t('Confirming with your casino'), tone: 'info' as const }
    }
    const copy = birdieBoardCopy(t as unknown as TFunction)
    if (roundNotOpen) return { message: copy.bettingUnavailable, tone: 'error' as const }
    if (tiles.length === 0 && phase.phase !== 'idle') {
      // No priced option at all: before `race_ready` that is simply "not yet",
      // afterwards it is a real "you cannot bet this round".
      const waiting = roundOdds === undefined && phase.phase === 'open'
      return {
        message: waiting ? copy.waitingForOdds : copy.bettingUnavailable,
        tone: (waiting ? 'info' : 'error') as 'error' | 'info'
      }
    }
    // Nothing persistent to say, and the transient case returned above.
    return null
  }, [notice, phase.phase, roundNotOpen, roundOdds, stageTwo, t, tiles.length])

  // ── Geometry + card ────────────────────────────────────────────────────
  // The slot is re-created whenever `.dolby-feed` re-renders its stream branch,
  // so it is re-read on a cheap interval rather than once at mount.
  const [slotEl, setSlotEl] = useState<HTMLElement | null>(null)
  useEffect(() => {
    if (isMobile) {
      setSlotEl(null)
      return undefined
    }
    const sync = (): void => {
      const found = document.querySelector(`.${BIRDIE_ARC_SLOT_CLASS}`)
      const next = found instanceof HTMLElement ? found : null
      setSlotEl((prev) => (prev === next ? prev : next))
    }
    sync()
    const probe = setInterval(sync, SLOT_PROBE_MS)
    return () => {
      clearInterval(probe)
    }
  }, [isMobile])

  // `useStageFrame` keys its observer on the REF OBJECT's identity, so a
  // plain `useRef` mutated after mount would never re-run it and the frame
  // would stay empty forever. A memoised ref is stable between renders and
  // changes exactly when the slot element does.
  const slotRef = useMemo<RefObject<HTMLElement | null>>(() => ({ current: slotEl }), [slotEl])
  const frame = useStageFrame(slotRef)
  // B14 — the platform's UI-scale model. ONE layout and ONE transform: the
  // board is laid out in `layoutFrame(frame)` (the reference box on any picture
  // up to 1280 wide, the picture itself above that, where `k` is capped) and
  // the picture box only decides where the scaled box sits and how big
  // `k` is. Before B14, `boardGeometry(frame)` ran on every frame change and
  // the px-clamped tiles collapsed as the window shrank while their type did
  // not; `boardGeometryFor` caches on the layout SIZE, so a letterbox offset
  // that moves costs nothing and a resize costs one solve.
  // Two placements (owner, 2026-09-08): while betting is open the columns sit
  // closer to the centre — nothing is rolling — and on `race_started` they slide
  // out to the playing placement, clear of the putt's path. `phase.phase`, not
  // `tilesLocked`: submitting bets locks the tiles but does not close the round.
  const layoutMode: BoardLayoutMode =
    phase.phase === 'open' || phase.phase === 'closing' ? 'betting' : 'playing'
  const geometry = useMemo(() => boardGeometryFor(frame, layoutMode), [frame, layoutMode])
  const scale = useMemo(() => frameScale(frame), [frame])
  // Layout px, from the SAME frame the tiles were placed in — the one transform
  // that scales them scales it too. B16b made the capsule MOVE: `cardBoxPx`
  // returns the centre-top box while betting and the top-right box from
  // `race_started` on, so all four properties change together and the
  // stylesheet eases them as one 320 ms slide.
  const cardStyle = useMemo<CSSProperties>(() => {
    const box = cardBoxPx(capsule.layout, geometry.frame)
    return { left: box.left, top: box.top, width: box.width, height: box.height }
  }, [capsule.layout, geometry.frame])
  // B34: the callout hangs under the CORNER box, right-aligned to it — the
  // capsule is there for every putt, whatever the layout mode.
  const calloutStyle = useMemo<CSSProperties>(() => {
    const box = cardBoxPx('condensed', geometry.frame)
    return {
      right: geometry.frame.width - (box.left + box.width),
      top: box.top + box.height + CALLOUT_GAP_PX
    }
  }, [geometry.frame])
  const calloutKey = `${currentRaceId ?? ''}-${callout?.putt ?? 0}`
  const calloutDesktop =
    callout && calloutCopy ? (
      <BirdieCallout
        animate={!reducedMotion}
        copy={calloutCopy}
        key={calloutKey}
        style={calloutStyle}
        view={callout}
      />
    ) : null
  const calloutStack =
    callout && calloutCopy ? (
      <BirdieCallout
        animate={!reducedMotion}
        className="birdie-callout--stack"
        copy={calloutCopy}
        key={calloutKey}
        view={callout}
      />
    ) : null
  // Keyed on the ROUND, not the beat: the pill arrives once and each numeral
  // changes inside it. The dial does its own per-beat remount.
  const teeOffKey = `teeoff-${currentRaceId ?? ''}`
  const teeOffDesktop =
    teeOff === undefined ? null : (
      <BirdieTeeOff beat={teeOff} eyebrow={t('Tee off')} key={teeOffKey} style={calloutStyle} />
    )
  const teeOffStack =
    teeOff === undefined ? null : (
      <BirdieTeeOff
        beat={teeOff}
        className="birdie-callout--stack"
        eyebrow={t('Tee off')}
        key={teeOffKey}
      />
    )
  const cardView = useMemo(
    () => (roundOdds ? buildBirdieCardView(roundOdds, started, language, t) : undefined),
    [language, roundOdds, started, t]
  )

  // B40 — the golfer's thought. Drawn from `livePutts`, which is the same value
  // the card's ring row and the avatar's reaction are drawn from, so the bubble
  // lands on the frame the ball does rather than on a clock of its own. The
  // golfer's NAME is the habit's seed: it is what a player knows him by, and it
  // is stable for as long as he is.
  const thought = useGolferThought({
    roundKey: currentRaceId,
    golferKey: cardView?.golferName,
    marks: livePutts,
    settled: phase.settlementVisible,
    // How likely each putt was, 0..1 — the card carries it as a percentage. It
    // is what decides whether a round is hard enough to be worth talking
    // through (`thoughtBudget`).
    ...(cardView === undefined ? {} : { makeRate: cardView.makeRatePercent / 100 }),
    // THE REVEALED type — off the `round_type` row (or the last putt row), held, which is the
    // same value the board's own Frost/Fire tiles are resolved with. Frost and
    // Fire are a market; nothing earlier than this reveal may reach the pool.
    ...(revealedType === undefined ? {} : { revealedType }),
    // The gap between the close and the first putt — the window the tee-off
    // count already owns, and the only place a line that knows nothing may go.
    teeingOff: phase.phase === 'locked',
    t
  })
  // B13: the wings' price-group captions are no longer drawn (owner: "the little
  // text you've added, one holed - all patterns 15.07x … better to hide it,
  // feels off and makes the ui look busy"), so the host no longer builds them.
  // `groupCaption` stays exported from <ArcOverlay> for the rules panel.

  const chipLabel = useCallback((amount: number) => formatNumberWithSuffix(amount, t), [t])
  /** A placed amount with its currency — the stake sentence's, the pill's and the sheet's. */
  const formatAmount = useCallback(
    (amount: number): string => `${currencySymbol}${chipLabel(amount)}`,
    [chipLabel, currencySymbol]
  )

  /**
   * What a tile with a stake on it says to a screen reader (B11 item 9 /
   * reviews 11-12). The tile's `aria-label` is its identity and price; this is
   * its state, hung off `aria-describedby` — which is also the only way the
   * amount and the settled outcome are announced at all, since an `aria-label`
   * hides the tile's own children (the chip stack and the win pill) from the
   * accessibility tree.
   */
  const stakeDescription = useCallback(
    ({
      amount,
      won,
      lost,
      free
    }: {
      amount: number
      won: boolean
      lost: boolean
      free?: boolean
    }): string => {
      // Interpolated OUTSIDE the `t()` call: the i18n extractor's options-bag
      // pattern is `\{[^}]*\}`, so a template literal inside the bag would end
      // the match early and the key would never reach the locale files (A25).
      const money = formatAmount(amount)
      // B38: two whole sentences rather than the free word concatenated onto the
      // ordinary one — the concatenation is exactly what B11 review 12 took out,
      // because a locale does not necessarily put the qualifier where English does.
      const parts = [
        free === true
          ? t('{{amount}} placed as a free bet', { amount: money })
          : t('{{amount}} placed', { amount: money })
      ]
      if (won) parts.push(t('Won'))
      else if (lost) parts.push(t('Lost'))
      return parts.join(' · ')
    },
    [formatAmount, t]
  )

  /**
   * The one word a free-bet chip carries, built here because `t` lives here and
   * the extractor is literal-only (A25) — `<ArcOverlay>` and `<StackBoard>` make
   * no translation call of their own, exactly as with the column headings.
   */
  const freeBetLabel = useMemo(() => t('Free'), [t])

  /**
   * The two column headings — since B20 the ONLY text on the board, and the
   * reason the tiles could drop their label rows. They are built HERE, not in
   * `<ArcOverlay>` / `<StackBoard>`, because the i18n extractor is literal-only
   * (A25): a translate call has to sit on a literal string in a file the extractor
   * walks, and the two board components make no translation call of their own.
   *
   * "Exact order" / "Any order" are the markets as a player reads them: three
   * putts in a stated sequence, versus how many drop with the sequence
   * ignored. Neither says "bet", "odds" or "win", so en-soc keeps them as they
   * are (`scripts/birdieLocales.mjs`).
   */
  const headings = useMemo(() => ({ exact: t('Exact order'), count: t('Any order') }), [t])

  /**
   * The ladder colour a placed TOTAL is drawn in. The tile's pile used to be a
   * fixed amber-brown whatever the player had bet with, which made it the one
   * chip on screen that did not agree with the bar it came from (B20 Task 2
   * item 4). `chips` is the live bar, so an operator's own values and currency
   * decide the rungs.
   */
  const tileChipStyle = useCallback(
    (amount: number): ChipStyle => chipStyleForAmount(amount, chips),
    [chips]
  )

  // ── Auto bet as betting zones (B32) ────────────────────────────────────
  // ORDER MATTERS. This hook is called AFTER the `latestType` effect above,
  // and that is not tidiness: on `race_ready` that effect dispatches the
  // board's `roundReset`, and the hook's own effect plans the open round.
  // Effects run in declaration order within a commit, so the plan lands on
  // the reset board — the other way round a `keep: false` reset would wipe
  // the plan it had just placed. Anything above that needs the hook (the
  // handlers' manual-edit note, the submission's wire result) goes through
  // `autoBetRef`, assigned right here.
  const autoBetCopy = useMemo(() => birdieAutoBetCopy(t), [t])
  const onAutoBetSkip = useCallback(
    (skip: AutoBetSkip) => {
      const rule = autoBetRef.current?.state.rule
      const zone = rule ? autoBetZoneText(autoBetCopy, rule.zone, formatMultiplierText) : ''
      const ceiling = rule ? formatAmount(rule.roundCeiling) : ''
      showNotice(autoBetSkipText(autoBetCopy, skip, { zone, ceiling }), 'info')
    },
    [autoBetCopy, formatAmount, showNotice]
  )
  // A round the ceiling trimmed. Not a skip — it placed — so it gets its own
  // sentence rather than borrowing a refusal's.
  const onAutoBetCapped = useCallback(
    (placed: number, caught: number) => {
      const rule = autoBetRef.current?.state.rule
      const ceiling = rule ? formatAmount(rule.roundCeiling) : ''
      showNotice(autoBetCopy.capped(placed, caught, ceiling), 'info')
    },
    [autoBetCopy, formatAmount, showNotice]
  )
  const onAutoBetStopped = useCallback(
    (reason: AutoBetStopReason) => {
      const sentence =
        reason === 'manual-edit' ? autoBetCopy.stopped.manualEdit : autoBetCopy.stopped[reason]
      showNotice(sentence, reason === 'balance' ? 'error' : 'info')
    },
    [autoBetCopy, showNotice]
  )
  // The window a finger could place in — the rule's, and the ghost's. One
  // expression so the two cannot drift apart.
  const canPlaceNow = limits.roundPriced && !phase.tilesLocked && !betsSubmitted
  const autoBet = useBirdieAutoBet({
    raceId: currentRaceId,
    tiles,
    limits,
    marketIdByOption,
    canPlace: canPlaceNow,
    dispatch,
    onSkip: onAutoBetSkip,
    onCapped: onAutoBetCapped,
    onStopped: onAutoBetStopped,
    playerId: player.id
  })
  autoBetRef.current = autoBet
  const [sheetOpen, setSheetOpen] = useState(false)
  // The sheet's draft. It starts on the default rule and is snapped onto the
  // strip's ladder on every open, because a stake the strip has no chip for
  // is a stake under the min bet on that currency. A sheet opened while a
  // rule is RUNNING shows that rule, not the last draft — the sheet's own
  // Update hands it back, and the running rule's count comes with it, so an
  // update that leaves Stop after alone keeps the rounds already spent.
  // Otherwise the draft is kept across opens, as the player left it.
  const [draft, setDraft] = useState<AutoBetRule>(DEFAULT_RULE)
  const chipValues = useMemo(() => chips.map((chip) => chip.value), [chips])
  const openAutoBetSheet = useCallback(() => {
    const running = autoBetRef.current?.state.rule ?? null
    setDraft((prev) => ruleOnLadder(running ?? prev, chipValues))
    setSheetOpen(true)
  }, [chipValues])
  const closeAutoBetSheet = useCallback(() => {
    setSheetOpen(false)
  }, [])
  // The ghost set: the tiles the armed rule passes over THIS round, planned
  // from the tiles and the rule rather than read off the board — a chip the
  // player added by hand does not un-ghost a tile, and the tiles a round the
  // ceiling refused would have caught are still the zone's. It ends with the
  // betting window: `ghostIdsFor` explains why the locked board must not
  // carry it.
  const armedRule = autoBet.state.rule
  const ghostIds = useMemo(
    () => ghostIdsFor({ tiles, rule: armedRule, limits, marketIdByOption, canPlace: canPlaceNow }),
    [armedRule, canPlaceNow, limits, marketIdByOption, tiles]
  )
  // The pill's line for a round the rule stood down on — for that round's
  // window, which is how long the machine keeps the race id it answered.
  const pillSkip =
    autoBet.state.plannedRaceId === currentRaceId ? autoBet.state.lastSkip : undefined
  // ENTRY, not placing: `canPlaceNow` above still gates every chip, and a rule
  // armed from the opener waits for the next round's prices — the machine only
  // plans an open, priced round. `tilesLocked` is the gate because the strip is
  // on screen exactly while it is false, so the opener and the strip's own auto
  // button can never be up together.
  const entryVisible = autoBetEntryVisible({
    armed: autoBet.armed,
    tilesLocked: phase.tilesLocked,
    betsSubmitted,
    chipCount: board.totals.chipCount,
    betsThisRound: currentBets.length
  })

  // B30: no viewport state and no resize / orientationchange listeners. The
  // mobile stack's sizes are custom properties on `.birdie-stack`, with the
  // small-phone floors carried by a media query, so the browser resolves them
  // and the host measures nothing.

  const overlay =
    !isMobile && slotEl !== null && frameIsUsable(frame)
      ? createPortal(
          <ArcOverlay
            betByOptionId={betByOptionId}
            chipLabel={chipLabel}
            chipStyleForAmount={tileChipStyle}
            currencySymbol={currencySymbol}
            enterKey={currentRaceId}
            frame={frame}
            freeBetIds={freeBetIds}
            freeBetLabel={freeBetLabel}
            geometry={geometry}
            headings={headings}
            notice={persistentNotice}
            onLockedPress={handleLockedPress}
            onPlace={handlePlace}
            onRemove={handleRemove}
            placement={board.state.chips}
            receivingOptionId={receivingOptionId}
            scale={scale}
            settlement={settlement}
            stakeDescription={stakeDescription}
            tiles={tiles}
            tilesLocked={phase.tilesLocked}
            liveStatus={live}
            puttsSeen={livePutts.length}
            roundType={boardWeather}
            seated={boardIsSeated}
            weatherStep={holedCount(livePutts)}
            winnerIds={winnerIds}
            zoneMissIds={ghostIds}
          >
            {cardView ? (
              // `<BirdieCard>` is absolutely positioned with NO coordinates of
              // its own, so the host hands it the capsule's box (`cardBoxPx`,
              // inside its own derived keep-out) — including the FIXED
              // height that pins it to 56 px whatever a stream-module `img` rule
              // does to its contents (B11 item 1, B12). B14: those are REFERENCE
              // px, so the capsule is a child of the scaled frame and is passed
              // through <ArcOverlay> rather than mounted beside it. B16: the box
              // is per capsule state, and the countdown rides on the avatar.
              <BirdieCard
                capsule={capsule}
                ring={ringView}
                rings={rings}
                roundType={boardWeather}
                style={cardStyle}
                {...(thought === undefined ? {} : { thought })}
                view={cardView}
                visible
              />
            ) : null}
            {calloutDesktop ?? teeOffDesktop}
          </ArcOverlay>,
          slotEl
        )
      : null

  return (
    // `tabIndex={-1}` adds no tab stop; it is the anchor the auto-bet sheet
    // hands focus back to when the pill that opened it is gone by the time it
    // closes, so the tab sequence resumes on the board and not at `<body>`.
    <div className="birdie-board" ref={boardRef} tabIndex={-1}>
      {overlay}

      {/*
        B11 item 8 / review 17: this was `isMobile && cardView`, so a round that
        carried no odds rendered NOTHING on a phone — not even the role="status"
        notice that says why. The board mounts whenever the device is mobile;
        the card strip is what is optional.
      */}
      {isMobile ? (
        <StackBoard
          {...(railSlot === undefined ? {} : { railSlot })}
          betByOptionId={betByOptionId}
          {...(cardView ? { card: cardView } : {})}
          capsule={capsule}
          chipLabel={chipLabel}
          chipStyleForAmount={tileChipStyle}
          currencySymbol={currencySymbol}
          enterKey={currentRaceId}
          freeBetIds={freeBetIds}
          freeBetLabel={freeBetLabel}
          headings={headings}
          notice={persistentNotice}
          onLockedPress={handleLockedPress}
          onPlace={handlePlace}
          onRemove={handleRemove}
          placement={board.state.chips}
          receivingOptionId={receivingOptionId}
          ring={ringView}
          rings={rings}
          stakeDescription={stakeDescription}
          tiles={tiles}
          tilesLocked={phase.tilesLocked}
          liveStatus={live}
          puttsSeen={livePutts.length}
          roundType={boardWeather}
          {...(thought === undefined ? {} : { thought })}
          callout={calloutStack ?? teeOffStack}
          winnerIds={winnerIds}
          zoneMissIds={ghostIds}
        />
      ) : null}

      {phase.settlementVisible && totalWin > 0 ? (
        <TotalWinBanner
          amount={totalWin}
          currencySymbol={currencySymbol}
          delayMs={TOTAL_WIN_DELAY_MS}
        />
      ) : null}

      {/*
        B32: one row directly above the strip, with one thing in it. Armed, it
        is the pill: the strip hides with the tiles, so the pill is then the
        one thing on screen that says a rule is running and the one way back to
        the sheet, and `pillSkip` swaps its line for the reason a round was
        skipped, for that round's window. Disarmed, it is the opener — the same
        capsule, quieter — and only while the strip is gone and nothing is
        riding on the round.
      */}
      {armedRule !== null || entryVisible ? (
        <div
          className={`birdie-board__autobet${phase.tilesLocked ? ' birdie-board__autobet--locked' : ''}`}
        >
          {armedRule !== null ? (
            <BirdieAutoBetPill
              formatAmount={formatAmount}
              formatMultiplier={formatMultiplierText}
              onOpen={openAutoBetSheet}
              onStop={autoBet.disarm}
              rule={armedRule}
              skip={pillSkip}
              t={t}
            />
          ) : (
            <BirdieAutoBetOpener onOpen={openAutoBetSheet} t={t} />
          )}
        </div>
      ) : null}

      {/*
        B31: the SHARED strip, not a Birdie copy of one. Everything the bar
        used to own — the ladder, Undo / Clear / ×2 / Repeat, the phone's
        3-chip slider — is `<ChipStrip>`'s markup and `chip-placement.scss`'s
        recipe, so a change to the twelve games' bar is a change to this one
        too. What stays here is what the strip deliberately does not know: the
        Birdie reducer every button dispatches into, and the sound each press
        makes.

        B32: the strip's own auto-bet toggle is the entry to the betting-zone
        rule — off, it opens the sheet; on, it disarms.
      */}
      <ChipStrip
        autoBet={autoBet.armed}
        autoSpinAt={autoBet.state.armedAtMs}
        barRef={chipBarRef}
        // `canRepeat` mirrors the reducer's `lastBets` test, but that is the
        // SECOND guard in the `repeat` case: the first refuses a round that
        // carried no odds (`round-unpriced`, birdieChips.ts). An unpriced
        // round does not lock the tiles, so without this the strip would show
        // a fully enabled Repeat whose only possible answer is a notice.
        canRepeat={limits.roundPriced && canRepeat(board.state)}
        // The ladder is a single-choice group — announce which chip is armed.
        chipAriaPressed
        chips={chips}
        doublePunchAt={doubleAnimAt}
        hasChips={board.totals.chipCount > 0}
        // `tilesLocked` is `betsSubmitted || locked || settled` — one flag for
        // both of the old bar's disabled rules.
        hidden={phase.tilesLocked}
        hideAutoBet={false}
        onClear={() => {
          autoBet.noteManualEdit()
          dispatch({ type: 'clear' })
          playEffect('cancel')
        }}
        onDouble={() => {
          const outcome = dispatch({ type: 'double' })
          playEffect('click')
          // A ×2 that grew something is a hand on the board, a refused one is
          // not — `partial-double` grew what it could and still counts.
          if (boardMoved(outcome)) autoBet.noteManualEdit()
          // Punch only when the board actually moved. The reducer says so:
          // `double-maxed` is "nothing grew" and `round-unpriced` is "no odds
          // to grow against", and both already raise their own notice. The
          // shared panel fires the keyframe on every press because its
          // handler has no such answer to read.
          if (outcome.notice === 'double-maxed' || outcome.notice === 'round-unpriced') return
          setDoubleAnimAt(0)
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setDoubleAnimAt(Date.now())
            })
          })
        }}
        onRepeat={() => {
          const outcome = dispatch({ type: 'repeat' })
          playEffect('click')
          // A replay the reducer refused put nothing on the board; a clamped
          // one did, so it turns the rule off like any other hand.
          if (boardMoved(outcome)) autoBet.noteManualEdit()
        }}
        onSelectChip={(index) => {
          setChipIndex(index)
          playEffect('click')
        }}
        onToggleAutoBet={autoBet.armed ? autoBet.disarm : openAutoBetSheet}
        onUndo={() => {
          autoBet.noteManualEdit()
          dispatch({ type: 'undo' })
          playEffect('click')
        }}
        selectedChipIdx={safeChipIndex}
      />

      {/* `scale` is 1 on mobile: `frameScale` returns 1 for the unmeasured
          (all-zero) frame the stack board leaves behind, so the phone's chip
          keeps its own 32-px size. */}
      <FlyingChipLayer chips={flying} label={chipLabel} scale={scale} />

      {sheetOpen ? (
        <BirdieAutoBetSheet
          armed={autoBet.armed}
          chipValues={chipValues}
          draft={draft}
          formatAmount={formatAmount}
          formatMultiplier={formatMultiplierText}
          onClose={closeAutoBetSheet}
          onDraftChange={setDraft}
          onRestoreFocus={() => {
            // The strip's auto button while the strip is up — it is hidden
            // with the tiles, and that is exactly the window in which the pill
            // was the only way in, so the board itself is the fallback.
            const auto = phase.tilesLocked
              ? null
              : (chipBarRef.current?.querySelector<HTMLElement>('.chip-panel__action-btn--auto') ??
                null)
            if (auto !== null) auto.focus()
            else boardRef.current?.focus()
          }}
          onStart={() => {
            autoBet.arm(draft)
            setSheetOpen(false)
          }}
          onStop={() => {
            autoBet.disarm()
            setSheetOpen(false)
          }}
          onUpdate={() => {
            // `arm` forgets the planned race, so the open round is planned
            // again under the new rule and `applyAutoBetPlan` clears the old
            // plan's chips before it places. A round already locked simply
            // takes the new rule next time.
            autoBet.arm(draft)
            setSheetOpen(false)
          }}
          preview={autoBet.previewFor(draft)}
          t={t}
          tiles={tiles}
        />
      ) : null}
    </div>
  )
}
